import { useQuery } from 'data-fetching-client';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GET_ALL_PRICING_PURCHASABLE_PRODUCTS } from 'pricing-pages-api/purchase-options/queries';
import { getI18nLanguage } from 'pricing-pages-ui-components/utils/languageUtils';
import { ANNUAL, MONTHLY } from 'self-service-api/constants/Terms';
import { useSetOneGlobalLoadingState } from '../context/GlobalLoadingStateContext';
import { isAppAuthSelector } from '../selectors/AuthSelectors';
import { useCountryCode, useDisplayedCurrencyCode } from './useCurrencies';
export const useQueryVariables = () => {
  const displayedCurrencyCode = useDisplayedCurrencyCode();
  const isAppAuth = useSelector(isAppAuthSelector);
  const language = (getI18nLanguage() || 'en').toUpperCase();
  const preferredCountryCode = useCountryCode();
  const memoizedVariables = useMemo(() => {
    return {
      noAuth: !isAppAuth,
      currencyCode: displayedCurrencyCode,
      language,
      preferredCountryCode: preferredCountryCode
    };
  }, [displayedCurrencyCode, isAppAuth, language, preferredCountryCode]);
  return memoizedVariables;
};
export const useBulkPurchaseOptions = ({
  onCompleted,
  onError,
  skip,
  variables
} = {}) => {
  var _data$bulkPurchaseOpt, _data$bulkPurchaseOpt2;
  const defaultQueryVariables = useQueryVariables();
  const onCompletedCallback = useSetOneGlobalLoadingState('PurchaseOptions', onCompleted);
  const onErrorCallback = useSetOneGlobalLoadingState('PurchaseOptions', onError);
  const {
    data,
    loading,
    error
  } = useQuery(GET_ALL_PRICING_PURCHASABLE_PRODUCTS, {
    onCompleted: onCompletedCallback,
    onError: onErrorCallback,
    skip: skip || !defaultQueryVariables.currencyCode || !defaultQueryVariables.language,
    variables: Object.assign({}, defaultQueryVariables, variables)
  });
  return {
    purchaseableProducts: data === null || data === void 0 || (_data$bulkPurchaseOpt = data.bulkPurchaseOptions) === null || _data$bulkPurchaseOpt === void 0 ? void 0 : _data$bulkPurchaseOpt.allPurchaseableProducts,
    releaseInsights: data === null || data === void 0 || (_data$bulkPurchaseOpt2 = data.bulkPurchaseOptions) === null || _data$bulkPurchaseOpt2 === void 0 ? void 0 : _data$bulkPurchaseOpt2.releaseInsights,
    loading,
    error
  };
};
export const usePurchaseableProductsForApiName = ({
  apiName,
  skip
}) => {
  var _purchaseableProducts;
  const {
    purchaseableProducts,
    loading,
    error
  } = useBulkPurchaseOptions({
    skip: skip || !apiName
  });
  if (!apiName) return {
    purchaseOptionsForApiName: null,
    loading,
    error
  };
  return {
    purchaseOptionsForApiName: purchaseableProducts === null || purchaseableProducts === void 0 || (_purchaseableProducts = purchaseableProducts[apiName]) === null || _purchaseableProducts === void 0 ? void 0 : _purchaseableProducts.purchaseOptions,
    loading,
    error
  };
};
export const usePurchaseableProductsForTerm = ({
  apiName,
  queryParamTerm,
  skip
}) => {
  const {
    purchaseOptionsForApiName,
    loading,
    error
  } = usePurchaseableProductsForApiName({
    apiName,
    skip
  });
  const purchaseOptionsForTerm = useMemo(() => (purchaseOptionsForApiName === null || purchaseOptionsForApiName === void 0 ? void 0 : purchaseOptionsForApiName.find(purchaseOption => {
    var _purchaseOption$offer;
    return (purchaseOption === null || purchaseOption === void 0 || (_purchaseOption$offer = purchaseOption.offerInfo) === null || _purchaseOption$offer === void 0 ? void 0 : _purchaseOption$offer.payment) === queryParamTerm;
  })) || (purchaseOptionsForApiName === null || purchaseOptionsForApiName === void 0 ? void 0 : purchaseOptionsForApiName[0]), [purchaseOptionsForApiName, queryParamTerm]);
  return {
    purchaseOptionsForTerm,
    loading,
    error
  };
};
export const useCalculatedPrice = ({
  apiName,
  queryParamTerm
}) => {
  var _purchaseOptionsForTe;
  const {
    purchaseOptionsForTerm,
    loading,
    error
  } = usePurchaseableProductsForTerm({
    apiName,
    queryParamTerm
  });
  return {
    monthlyCalculatedPrice: purchaseOptionsForTerm === null || purchaseOptionsForTerm === void 0 ? void 0 : purchaseOptionsForTerm.monthly.calculatedPrice,
    oneTimeFeeCalculatedPrice: purchaseOptionsForTerm === null || purchaseOptionsForTerm === void 0 || (_purchaseOptionsForTe = purchaseOptionsForTerm.oneTimeFee) === null || _purchaseOptionsForTe === void 0 ? void 0 : _purchaseOptionsForTe.calculatedPrice,
    annualCalculatedPrice: purchaseOptionsForTerm === null || purchaseOptionsForTerm === void 0 ? void 0 : purchaseOptionsForTerm.annually.calculatedPrice,
    loading,
    error
  };
};
export const useListPrice = ({
  apiName,
  queryParamTerm
}) => {
  const {
    purchaseOptionsForTerm,
    loading,
    error
  } = usePurchaseableProductsForTerm({
    apiName,
    queryParamTerm
  });
  return {
    monthlyListPrice: purchaseOptionsForTerm === null || purchaseOptionsForTerm === void 0 ? void 0 : purchaseOptionsForTerm.monthly.fullPrice,
    monthlyDiscountedPrice: purchaseOptionsForTerm === null || purchaseOptionsForTerm === void 0 ? void 0 : purchaseOptionsForTerm.monthly.calculatedPrice,
    annualListPrice: purchaseOptionsForTerm === null || purchaseOptionsForTerm === void 0 ? void 0 : purchaseOptionsForTerm.annually.fullPrice,
    annualDiscountedPrice: purchaseOptionsForTerm === null || purchaseOptionsForTerm === void 0 ? void 0 : purchaseOptionsForTerm.annually.calculatedPrice,
    loading,
    error
  };
};
export const useHighestPossibleMonthlyPrice = ({
  apiName
}) => {
  const {
    monthlyListPrice: monthlyListPriceOnAnnualTerms = 0,
    loading: annualCalculatedPriceLoading,
    error: annualCalculatedPriceError
  } = useListPrice({
    apiName,
    queryParamTerm: ANNUAL
  });
  const {
    monthlyListPrice: monthlyListPriceOnMonthlyTerms = 0,
    loading: monthlyCalculatedPriceLoading,
    error: monthlyCalculatedPriceError
  } = useListPrice({
    apiName,
    queryParamTerm: MONTHLY
  });
  const loading = annualCalculatedPriceLoading || monthlyCalculatedPriceLoading;
  const errors = [annualCalculatedPriceError, monthlyCalculatedPriceError].filter(error => !!error);
  return {
    highestPossibleMonthlyPrice: monthlyListPriceOnAnnualTerms > monthlyListPriceOnMonthlyTerms ? monthlyListPriceOnAnnualTerms : monthlyListPriceOnMonthlyTerms,
    loading,
    error: errors.length > 0 ? errors : null
  };
};
export const useReleaseInsights = ({
  onCompleted,
  onError,
  skip
}) => {
  const {
    releaseInsights,
    loading,
    error
  } = useBulkPurchaseOptions({
    onCompleted,
    onError,
    skip
  });
  return {
    releaseInsights,
    loading,
    error
  };
};
export const useHasReleaseInsight = ({
  onCompleted,
  onError,
  skip,
  releaseInsight
}) => {
  const {
    releaseInsights,
    loading,
    error
  } = useReleaseInsights({
    onCompleted,
    onError,
    skip: !releaseInsight || skip
  });
  const hasReleaseInsight = !!(releaseInsights !== null && releaseInsights !== void 0 && releaseInsights[releaseInsight]);
  return {
    hasReleaseInsight,
    loading,
    error
  };
};

/**
 * @deprecated Temporary workaround for PPUIv1 - DO NOT USE OR COPY THIS IN PPUI V2
 * @description This hook returns the recurring charge purchase options for a list of api names.
 * Non-recurring products in the legacy bulk request have paymentTerm and commitmentTerm set to 0.
 *
 * @param {ProductApiName[]} props.apiNames - The api names to get the recurring charge purchase options for
 */
const useLegacyPurchaseOptionsForRecurringChargeApiNames = ({
  apiNames
}) => {
  const {
    purchaseableProducts,
    loading,
    error
  } = useBulkPurchaseOptions({
    skip: !(apiNames !== null && apiNames !== void 0 && apiNames.length)
  });
  const recurringChargePurchaseOptions = useMemo(() => {
    if (!purchaseableProducts) return [];
    if (!(apiNames !== null && apiNames !== void 0 && apiNames.every(apiName => purchaseableProducts[apiName]))) return [];

    // Filter out one-time fee products (paymentTerm = commitmentTerm = 0) since they have no common terms with recurring products
    // (They can still be purchased with other products or standalone)
    return apiNames.filter(apiName => {
      var _purchaseableProducts2;
      return (_purchaseableProducts2 = purchaseableProducts[apiName]) === null || _purchaseableProducts2 === void 0 ? void 0 : _purchaseableProducts2.purchaseOptions.some(purchaseOption => purchaseOption.paymentTerm > 0 && purchaseOption.commitmentTerm > 0);
    }).map(apiName => {
      var _purchaseableProducts3;
      return purchaseableProducts === null || purchaseableProducts === void 0 || (_purchaseableProducts3 = purchaseableProducts[apiName]) === null || _purchaseableProducts3 === void 0 ? void 0 : _purchaseableProducts3.purchaseOptions;
    });
  }, [apiNames, purchaseableProducts]);
  return {
    recurringChargePurchaseOptions,
    loading,
    error
  };
};

/**
 * Sorts purchase options by lowest commitment term first, then lowest payment term
 * MCMP -> ACMP -> ACAP
 */
const sortPurchaseOptionsByTerms = (a, b) => {
  if (a.commitmentTerm < b.commitmentTerm) return -1;
  if (a.commitmentTerm > b.commitmentTerm) return 1;
  if (a.paymentTerm < b.paymentTerm) return -1;
  if (a.paymentTerm > b.paymentTerm) return 1;
  return 0;
};

/**
 * @deprecated Temporary workaround for PPUIv1 - DO NOT USE OR COPY THIS IN PPUI V2
 * @description This hook returns the purchase terms for a list of api names. Terms are sorted by lowest commitment term first, then lowest payment term.
 *
 * @example
 * const { purchaseTermsForApiNames, loading, error } = useLegacyPurchaseTermsForApiNames({
 *   apiNames: [Products.MARKETING_HUB_STARTER, Products.SALES_HUB_STARTER],
 * });
 *
 * const [monthlyTerm, annualTerm] = purchaseTermsForApiNames;
 */
const useLegacyPurchaseTermsForApiNames = ({
  apiNames
}) => {
  const {
    recurringChargePurchaseOptions,
    loading,
    error
  } = useLegacyPurchaseOptionsForRecurringChargeApiNames({
    apiNames
  });

  // Takes each purchase option and returns the paymentTerm and commitmentTerm
  const purchaseTermsList = useMemo(() => {
    if (!recurringChargePurchaseOptions || !recurringChargePurchaseOptions.length) {
      return [];
    }
    return recurringChargePurchaseOptions.map(purchaseOptions => {
      return [...(purchaseOptions || [])].sort(sortPurchaseOptionsByTerms).map(purchaseOption => {
        return {
          paymentTerm: purchaseOption.paymentTerm >= 12 ? 'ANNUAL' : 'MONTHLY',
          commitmentTerm: purchaseOption.commitmentTerm >= 12 ? 'ANNUAL' : 'MONTHLY'
        };
      });
    });
  }, [recurringChargePurchaseOptions]);
  return {
    purchaseTermsForApiNames: purchaseTermsList,
    loading,
    error
  };
};

/**
 * @description Filters to the common terms across all term lists e.g.
 * [ [ ACAP, ACMP], [ACAP, MCMP] ] -> [ ACAP ]
 * @param listOfTermLists - A list of term lists
 * @returns The common terms across all term lists
 */
const filterCommonTerms = listOfTermLists => {
  return listOfTermLists[0].filter(term =>
  // does every list contain the matching term
  listOfTermLists.every(termList => termList.some(t => t.paymentTerm === term.paymentTerm && t.commitmentTerm === term.commitmentTerm)));
};

/**
 * @deprecated Temporary workaround for PPUIv1 - DO NOT USE OR COPY THIS IN PPUI V2
 * @description This hook returns the common purchase terms for a list of api names.
 *
 * @example
 * const { commonTerms, loading, error } = useLegacyCommonTermForProducts({
 *   apiNames: [Products.MARKETING_HUB_STARTER, Products.SALES_HUB_STARTER],
 *   queryParamTerm: 'ANNUAL',
 * });
 *
 * const [monthlyTerm, annualTerm] = commonTerms;
 */
export const useLegacyAllCommonTermsForProducts = ({
  apiNames
}) => {
  const {
    purchaseTermsForApiNames,
    loading,
    error
  } = useLegacyPurchaseTermsForApiNames({
    apiNames
  });

  // Filters to the common terms across all recurring charge api names
  const commonTerms = useMemo(() => {
    if (!(purchaseTermsForApiNames !== null && purchaseTermsForApiNames !== void 0 && purchaseTermsForApiNames.length)) return [];
    if (purchaseTermsForApiNames.length === 1) return purchaseTermsForApiNames[0];
    return filterCommonTerms(purchaseTermsForApiNames);
  }, [purchaseTermsForApiNames]);
  return {
    commonTerms,
    loading,
    error
  };
};

/**
 * @deprecated Temporary workaround for PPUIv1 - DO NOT USE OR COPY THIS IN PPUI V2
 * @description This hook returns the common available term for a list of api names.
 *
 * @example
 * const { commonTerm, loading, error } = useLegacyCommonTermForProducts({
 *   apiNames: [Products.SALES_HUB_PROFESSIONAL, Products.SERVICE_HUB_PROFESSIONAL],
 *   queryParamTerm: 'MONTHLY',
 * });
 *
 * const commitmentTermToUse = commonTerm.commitmentTerm; // 'ANNUAL'
 * const paymentTermToUse = commonTerm.paymentTerm; // 'MONTHLY'
 */
export const useLegacyCommonTermForProducts = ({
  apiNames,
  queryParamTerm = MONTHLY
}) => {
  const {
    commonTerms,
    loading,
    error
  } = useLegacyAllCommonTermsForProducts({
    apiNames
  });
  if (!(commonTerms !== null && commonTerms !== void 0 && commonTerms.length)) return {
    commonTerm: null,
    loading,
    error
  };
  if (commonTerms.length === 1) return {
    commonTerm: commonTerms[0],
    loading,
    error
  };
  const [monthlyTerm, annualTerm] = commonTerms;
  const commonTerm = queryParamTerm === ANNUAL ? annualTerm : monthlyTerm;
  return {
    commonTerm,
    loading,
    error
  };
};